import Box from "@mui/material/Box";
import { useCallback, useEffect, useState } from "react";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { useGetBankAccountsService } from "subModule/src/services/bankaccount/useBankAccount";
import { useSnackbar } from "notistack";
import AlertMessages from "common/Alert/AlertMessages";
import { useTranslation } from "react-i18next";
import { Grid, SelectChangeEvent, Typography } from "@mui/material";
import { checkPrivileges } from "Helpers/CheckPrivileges";
import { useNavigate, useSearchParams } from "react-router-dom";
import FormDialog from "common/Modal/Modal";
import ComponentHeaderTwo from "common/ComponentHeaderTwo/ComponentHeaderTwo";
import { ReactComponent as Bank } from "assets/SideBarIcons/bank.svg";
import BackendPaginationTable from "common/Table/TableWithPagination";
import BankAccountSearchHeader from "components/BankAccount/BankAccountSearchHeader";
import { BankAccountProfileService } from "subModule/src/core/services";
import {
  BankAccountResponse,
  ChangeStatusBankAccountResponse,
} from "subModule/src/services/bankaccount/interface";
import { DeleteData } from "subModule/src/type";
import Loader from "layout/Loader";
import { useSelector } from "react-redux";
import { selectUserInfo } from "features/user/userSlice";

interface SearchFormData {
  [key: string]: string | any;
}

export default function BankAccountListing() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [openDeleteModal, setDeleteModal] = useState(false);
  const [pageNo, setPageNo] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(20);
  const [statusModal, setStatusModal] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [filter, setFilter] = useState<string[]>(["accountTitle", "type"]);
  const [search, setSearch] = useState<any>({
    type: Number(searchParams?.get("type")) || null,
  });
  const [row, setRow] = useState<BankAccountResponse>({});
  const loggedInUserCurrency = useSelector(selectUserInfo)?.business?.currency;
  const [searchFormData, setSearchFormData] = useState<SearchFormData>({
    accountTitle: "",
    bankName: "",
    accountNumber: "",
    type: Number(searchParams?.get("type")) || null,
  });
  const {
    data: tableData,
    isFetching,
    refetch,
  } = useGetBankAccountsService({
    pageNumber: pageNo,
    size: pageSize,
    ...search,
  });
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const fetchData = useCallback(() => {
    refetch()
      .then((res: any) => {
        if (!res?.data?.pages[0]?.success)
          enqueueSnackbar(
            <AlertMessages
              text={res?.data?.pages[0]?.message || "Something went wrong"}
            />,
            { variant: "error" },
          );
      })
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => {
    fetchData();
  }, [pageNo, pageSize, search]);
  useEffect(() => {
    if (loading) handleSubmitSearch();
  }, [searchFormData.type]);
  const accountTypes = [
    { value: 10, label: t("cash") },
    { value: 20, label: t("bank") },
  ];
  const handleChangePage = (pageNo: number) => {
    setPageNo(pageNo + 1);
  };
  const handleChangeRows = (pageSize: number) => {
    setPageSize(pageSize);
    setPageNo(1);
  };
  const onEdit = (row: any) => {
    if (row.default === false) navigate("/account/" + row.id);
  };
  const showRowView = (row: any) => {
    navigate(`/account/${row.accountTitle}/${row.id}/ledger`);
  };
  const onDelete = (row: any) => {
    if (row.default === false) {
      setDeleteModal(true);
      setRow(row);
    }
  };
  const clearSearch = () => {
    setFilter(["accountTitle", "type"]);
    setSearchFormData({
      accountTitle: "",
      bankName: "",
      accountNumber: "",
      type: Number(searchParams?.get("type")) || null,
    });
    if (Object.keys(search).length) {
      setSearch({ type: Number(searchParams?.get("type")) || null });
    }
  };
  const handleChangeFilter = (event: SelectChangeEvent<typeof filter>) => {
    const {
      target: { value },
    } = event;
    setFilter(typeof value === "string" ? value.split(",") : value);
    setSearchFormData((prevFormData) =>
      Object.fromEntries(
        Object.keys(prevFormData).map((key) => [
          key,
          value.includes(key) ? prevFormData[key] : "",
        ]),
      ),
    );
    if (!value.length && Object.keys(search).length) {
      clearSearch();
    }
  };
  const handleChangeSearch = (field: string, value: any) => {
    const val = field === "type" ? (value ? value.value : "") : value;
    if (field === "type" && searchFormData.type === val) return;
    setSearchFormData((prevData) => ({
      ...prevData,
      [field]: val,
    }));
    if (field === "type") setLoading(true);
  };
  const handleSubmitSearch = () => {
    const filteredSearch: Record<string, string | number | boolean> = {};
    Object.entries(searchFormData).forEach(([key, value]) => {
      if (value) {
        filteredSearch[key] = typeof value === "string" ? value?.trim() : value;
      }
    });
    if (
      Object.keys(filteredSearch).length > 0 &&
      Object.values(filteredSearch).some((value) => value)
    ) {
      setSearch(filteredSearch);
      setPageNo(1);
      setPageSize(pageSize);
    }
  };
  const onChangeStatus = (row: any) => {
    if (row.default === false) {
      setRow(row);
      setStatusModal(true);
    }
  };
  const actionslist = [
    { label: "edit", onClick: onEdit, icon: <EditIcon /> },
    { label: "delete", onClick: onDelete, icon: <DeleteIcon /> },
  ];
  const adjustActions = (actions: any[]) => {
    return checkPrivileges("ROLE_BANKACCOUNT_UPDATE")
      ? checkPrivileges("ROLE_BANKACCOUNT_ARCHIVE")
        ? actions
        : actions.filter((action) => action.label !== "delete")
      : checkPrivileges("ROLE_BANKACCOUNT_ARCHIVE")
        ? actions.filter((action) => action.label !== "edit")
        : [];
  };
  return (
    <Box>
      <ComponentHeaderTwo
        showBtn={true}
        showSaveBtn={false}
        onClick={() => navigate("/account/create")}
        heading={t("accounts")}
        icon={<Bank height={28} width={28} />}
        btnText={"create"}
        btnType={"create"}
        showCreateBtn={checkPrivileges("ROLE_BANKACCOUNT_CREATE")}
      />
      {isFetching || loading ? (
        <Loader />
      ) : (
        <BackendPaginationTable
          columns={[
            {
              id: "accountTitle",
              label: "accountTitle",
              numeric: false,
              width: "18%",
            },
            {
              id: "bankName",
              label: "bankName",
              numeric: false,
              width: "14%",
            },
            {
              id: "accountNumber",
              label: "accountNumber",
              numeric: false,
              width: "14%",
            },
            {
              id: "accountType",
              label: "type",
              numeric: false,
              width: "8%",
            },
            {
              id: "paymentModeTitle",
              label: "PaymentMode",
              numeric: false,
              width: "12%",
            },
            {
              id: "default",
              label: "isDefault",
              numeric: false,
              width: "8%",
            },
            {
              id: "currentBalance",
              label: "balance",
              numeric: false,
              width: "12%",
            },
            ...(checkPrivileges("ROLE_BANKACCOUNT_ACTIVE")
              ? [
                  {
                    id: "active",
                    label: "status",
                    numeric: false,
                    width: "8%",
                  },
                ]
              : []),
            ...(adjustActions(actionslist).length
              ? [
                  {
                    id: "actions",
                    label: "actions",
                    numeric: false,
                    width: "5%",
                  },
                ]
              : []),
          ]}
          data={tableData?.pages[0]?.data?.content || []}
          pageNo={pageNo}
          pageSize={pageSize}
          tableData={tableData?.pages[0]?.data || {}}
          showCheckbox={false}
          showActions={true}
          enableSorting={true}
          showHeader={true}
          showSearch={true}
          showFilter={true}
          showCreateButton={true}
          actions={adjustActions(actionslist)}
          handleChangePage={handleChangePage}
          handleChangeRows={handleChangeRows}
          onChangeStatus={onChangeStatus}
          rowClick={checkPrivileges("ROLE_BANKACCOUNT_UPDATE")}
          showRowView={showRowView}
          loadingSearch={false}
          currency={loggedInUserCurrency}
          headerComponent={
            <BankAccountSearchHeader
              searchFormData={searchFormData}
              handleChangeFilter={handleChangeFilter}
              handleChangeSearch={handleChangeSearch}
              handleSubmitSearch={handleSubmitSearch}
              clearSearch={clearSearch}
              {...{ filter, search, accountTypes }}
            />
          }
        />
      )}
      {openDeleteModal && (
        <FormDialog
          open={openDeleteModal}
          onClose={() => setDeleteModal(false)}
          title={t("delete") + " " + t("ACCOUNT")}
          content={
            <Grid container>
              <Grid item xs={12}>
                <Typography>{t("Are you sure you want to delete?")}</Typography>
              </Grid>
            </Grid>
          }
          saveFunction={() => {
            setDeleteModal(false);
            setLoading(true);
            BankAccountProfileService.deleteBankAccount((row as any).id)
              .then((response: DeleteData) => {
                if (response.success) {
                  if (
                    tableData?.pages[0]?.data?.content?.length === 1 &&
                    pageNo > 1
                  )
                    setPageNo(1);
                  else refetch();
                  enqueueSnackbar(
                    <AlertMessages
                      statusCode={response.status}
                      text={
                        t("ACCOUNT") +
                        " " +
                        t("deleted") +
                        " " +
                        t("successfully")
                      }
                    />,
                    { variant: "success" },
                  );
                } else {
                  enqueueSnackbar(
                    <AlertMessages statusCode={response.status} />,
                    { variant: "error" },
                  );
                }
              })
              .catch((err: any) => {
                console.error("===", err);
                enqueueSnackbar(
                  <AlertMessages
                    text={err?.data?.message || err || "Something went wrong"}
                  />,
                  { variant: "error" },
                );
              })
              .finally(() => setLoading(false));
          }}
          submitButtonText="Delete"
        />
      )}
      {statusModal && (
        <FormDialog
          open={statusModal}
          onClose={() => setStatusModal(false)}
          title="Change Status"
          // icon={<div><Home/></div>}
          content={
            <Grid container>
              <Grid item xs={12}>
                <Typography>{t(`Are you sure?`)}</Typography>
              </Grid>
            </Grid>
          }
          saveFunction={() => {
            setStatusModal(false);
            setLoading(true);
            BankAccountProfileService.changeStatusBankAccount(
              (row as any).id,
              row.active ? "inactive" : "active",
              {},
            )
              .then((response: ChangeStatusBankAccountResponse) => {
                if (response.success) {
                  refetch();
                  enqueueSnackbar(
                    <AlertMessages
                      statusCode={response.status}
                      text={
                        t("status") +
                        " " +
                        t("changed") +
                        " " +
                        t("successfully")
                      }
                    />,
                    { variant: "success" },
                  );
                } else {
                  enqueueSnackbar(
                    <AlertMessages statusCode={response.status} />,
                    { variant: "error" },
                  );
                }
              })
              .catch((err: any) => {
                console.error("===", err);
                enqueueSnackbar(
                  <AlertMessages
                    text={err?.data?.message || err || "Something went wrong"}
                  />,
                  { variant: "error" },
                );
              })
              .finally(() => setLoading(false));
          }}
          submitButtonText="OK"
        />
      )}
    </Box>
  );
}
